import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { useCart } from "../contexts/CartContext";

const ReturnContent = () => {
    const [params] = useSearchParams();
    const orderId = params.get("order_id");
    const failedHint = params.get("status") === "failed";
    const navigate = useNavigate();
    const { fetchCart } = useCart();

    const [statusObj, setStatusObj] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [terminal, setTerminal] = useState(false);

    useEffect(() => {
        if (!orderId) return;
        let active = true;
        let interval;
        const startedAt = Date.now();
        let consecutiveErrors = 0;

        const poll = async () => {
            try {
                const { data } = await api.get(`/orders/${orderId}/payment-status`);
                if (!active) return;
                consecutiveErrors = 0;
                setStatusObj(data);
                setElapsed(Math.floor((Date.now() - startedAt) / 1000));
                if (data.paid || data.payment_status === "failed" || data.status === "cancelled") {
                    setTerminal(true);
                    if (data.paid) fetchCart();
                    clearInterval(interval);
                }
            } catch {
                consecutiveErrors += 1;
                if (consecutiveErrors >= 5) {
                    setTerminal(true);
                    clearInterval(interval);
                }
            }
            if (Date.now() - startedAt > 60_000) {
                setTerminal(true);
                clearInterval(interval);
            }
        };
        poll();
        interval = setInterval(poll, 2000);
        return () => { active = false; clearInterval(interval); };
    }, [orderId, fetchCart]);

    if (!orderId) {
        return (
            <div className="rounded-3xl border border-zinc-100 bg-white p-10 text-center shadow-soft">
                <h2 className="text-xl font-semibold">Missing order reference</h2>
                <p className="mt-2 text-sm text-zinc-500">No order_id was provided in the return URL.</p>
                <Link to="/cart" className="btn-brand mt-6 inline-flex">Back to cart</Link>
            </div>
        );
    }

    const isSuccess = statusObj?.paid;
    const isFailed = !isSuccess && (failedHint || statusObj?.payment_status === "failed" || statusObj?.status === "cancelled");

    return (
        <div className="mx-auto max-w-2xl">
            <div className="rounded-3xl border border-zinc-100 bg-white p-10 text-center shadow-soft" data-testid="checkout-return">
                {!terminal && (
                    <>
                        <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-brand-50">
                            <Loader2 className="h-8 w-8 animate-spin text-brand-700" />
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight">Confirming your payment…</h2>
                        <p className="mt-2 text-sm text-zinc-500">Waiting for confirmation from Zoho Payments. This may take a few seconds.</p>
                        <p className="mt-1 text-xs text-zinc-400">Order {statusObj?.order_no || ""} • elapsed {elapsed}s</p>
                    </>
                )}

                {terminal && isSuccess && (
                    <>
                        <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-50">
                            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight" data-testid="return-success-title">Order confirmed</h2>
                        <p className="mt-2 text-sm text-zinc-600">Your payment was received. We'll get your medicines packed and shipped soon.</p>
                        <p className="mt-1 text-xs text-zinc-400">Order # {statusObj?.order_no}</p>
                        <div className="mt-6 flex flex-col items-center gap-2">
                            <Link to={`/orders/${orderId}`} className="btn-brand" data-testid="return-view-order-btn">
                                View order details
                            </Link>
                            <Link to="/products" className="text-sm text-brand-700 hover:underline">Continue shopping</Link>
                        </div>
                    </>
                )}

                {terminal && isFailed && (
                    <>
                        <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-red-50">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight" data-testid="return-failed-title">Payment did not complete</h2>
                        <p className="mt-2 text-sm text-zinc-600">Your card or account wasn't charged. You can try again or change payment method.</p>
                        <div className="mt-6 flex flex-col items-center gap-2">
                            <button onClick={() => navigate("/cart")} className="btn-brand">
                                Back to cart
                            </button>
                            <Link to={`/orders/${orderId}`} className="text-sm text-brand-700 hover:underline">View attempted order</Link>
                        </div>
                    </>
                )}

                {terminal && !isSuccess && !isFailed && (
                    <>
                        <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-amber-50">
                            <AlertCircle className="h-8 w-8 text-amber-600" />
                        </div>
                        <h2 className="text-2xl font-semibold tracking-tight" data-testid="return-pending-title">Still processing</h2>
                        <p className="mt-2 text-sm text-zinc-600">It's taking a bit longer than usual to confirm. We'll update your order status as soon as Zoho notifies us.</p>
                        <div className="mt-6 flex flex-col items-center gap-2">
                            <Link to={`/orders/${orderId}`} className="btn-brand">View order</Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const CheckoutReturn = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-16 lg:px-12">
            <AuthGuard>
                <ReturnContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default CheckoutReturn;
